import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { useData } from '../contexts/DataContext';
import { formatZAR } from '../utils/currency';
import { Sparkles, Heart, Search, PenTool, CheckCircle2, Flower2 } from 'lucide-react';
export function FlowersPage() {
  const {
    products
  } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  // Filter for flower products only
  const flowerProducts = products.filter(product => product.category.toLowerCase().includes('flower'));
  // Apply search filter
  const filteredProducts = flowerProducts.filter(product => product.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleCustomize = (productId: string) => {
    navigate('/occasions');
  };
  const showcaseImages = ['https://cdn.magicpatterns.com/uploads/wcJDhNbZUQMS3xhEvZNn5U/4263e3976edc01aa21a1ddc0473c2d64.jpg', 'https://cdn.magicpatterns.com/uploads/9TamqVGh6LUKukVBR6Pmpj/bb2f01e2146382daf0bbb0749124a0d3.jpg', 'https://cdn.magicpatterns.com/uploads/4xi3fbKbtn9T9Xw384aMau/b4543cb24c0eef95a1953d6b6dcdc913.jpg', 'https://cdn.magicpatterns.com/uploads/amuV6p5x8WmUQ76ThgQVNG/daadf10acd060c935b9bff0e9e4fae7c.jpg', 'https://cdn.magicpatterns.com/uploads/4BrPUQ7zh7ULcn3JakqFA2/a49d82d18cb7f45d34bd5179e5abd504.jpg'];
  return <div className="min-h-screen bg-white" data-id="element-2399">
      <Navbar data-id="element-2400" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-gradient-to-b from-rose-50/50 to-white" data-id="element-2401">
        {/* Decorative Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-2402">
          <motion.div animate={{
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-20 left-[10%] w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2403" />
          <motion.div animate={{
          y: [0, 30, 0],
          rotate: [0, -5, 0]
        }} transition={{
          duration: 7,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute top-40 right-[10%] w-72 h-72 bg-rose-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30" data-id="element-2404" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2405">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-2406">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-rose-100 shadow-sm mb-6" data-id="element-2407">
              <Flower2 className="w-4 h-4 text-rose-400 mr-2" data-id="element-2408" />
              <span className="text-sm font-medium text-gray-600" data-id="element-2409">
                Fresh Blooms Daily
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6" data-id="element-2410">
              Flowers That Speak <br data-id="element-2411" />
              <span className="text-rose-500" data-id="element-2412">From the Heart</span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8" data-id="element-2413">
              Express your feelings with our stunning collection of fresh flower
              arrangements. Hand-picked and beautifully crafted to make every
              moment memorable.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2414">
              <Button size="lg" onClick={() => document.getElementById('products')?.scrollIntoView({
              behavior: 'smooth'
            })} rightIcon={<Heart className="w-5 h-5" data-id="element-2416" />} data-id="element-2415">
                Browse Collection
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/occasions')} data-id="element-2417">
                Custom Arrangements
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Personalized Showcase Section */}
      <section className="py-20 bg-white overflow-hidden" data-id="element-2418">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2419">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2420">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2421">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-rose-50 border border-rose-100 shadow-sm mb-6" data-id="element-2422">
                <Sparkles className="w-4 h-4 text-rose-500 mr-2" data-id="element-2423" />
                <span className="text-sm font-medium text-rose-900" data-id="element-2424">
                  Signature Collection
                </span>
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-2425">
                Fresh Flower Arrangements <br data-id="element-2426" />
                <span className="text-rose-500" data-id="element-2427">Crafted with Care</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-2428">
                From elegant rose boxes to mixed seasonal bouquets, our florists
                create stunning arrangements that capture the essence of your
                celebration.
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-10" data-id="element-2429">
                <div className="bg-rose-50 px-6 py-4 rounded-2xl border border-rose-100 shadow-sm" data-id="element-2430">
                  <span className="block text-sm text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-2431">
                    Starting from
                  </span>
                  <span className="block text-4xl font-bold text-rose-600" data-id="element-2432">
                    R450
                  </span>
                </div>
                <div className="h-16 w-px bg-gray-200 hidden sm:block" data-id="element-2433"></div>
                <div className="flex flex-col gap-2" data-id="element-2434">
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2435">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2436" />{' '}
                    Fresh Daily
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2437">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2438" />{' '}
                    Custom Arrangements
                  </span>
                  <span className="flex items-center text-gray-700 font-medium" data-id="element-2439">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2" data-id="element-2440" />{' '}
                    Same-Day Delivery
                  </span>
                </div>
              </div>

              <Button size="lg" onClick={() => navigate('/occasions')} className="w-full sm:w-auto shadow-lg shadow-rose-200" rightIcon={<PenTool className="w-5 h-5" data-id="element-2442" />} data-id="element-2441">
                Order Custom Arrangement
              </Button>
            </motion.div>

            <div className="grid grid-cols-2 gap-4 sm:gap-6" data-id="element-2443">
              <div className="space-y-4 sm:space-y-6" data-id="element-2444">
                {showcaseImages.slice(0, 2).map((img, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: idx * 0.15
              }} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/5] group cursor-pointer" data-id="element-2445">
                    <img src={img} alt={`Flower Arrangement ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-2446" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4" data-id="element-2447">
                      <span className="text-white font-medium text-sm flex items-center" data-id="element-2448">
                        <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-2449" /> View
                        Design
                      </span>
                    </div>
                  </motion.div>)}
              </div>
              <div className="space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-2450">
                {showcaseImages.slice(2, 4).map((img, idx) => <motion.div key={idx + 2} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: (idx + 2) * 0.15
              }} className="relative rounded-2xl overflow-hidden shadow-lg aspect-[4/5] group cursor-pointer" data-id="element-2451">
                    <img src={img} alt={`Flower Arrangement ${idx + 3}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-2452" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4" data-id="element-2453">
                      <span className="text-white font-medium text-sm flex items-center" data-id="element-2454">
                        <Heart className="w-4 h-4 mr-2 fill-current" data-id="element-2455" /> View
                        Design
                      </span>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-16 bg-gray-50" data-id="element-2456">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2457">
          <div className="text-center mb-12" data-id="element-2458">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4" data-id="element-2459">
              Explore Our Collection
            </h2>
            <div className="max-w-md mx-auto relative" data-id="element-2460">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-2461" />
              <input type="text" placeholder="Search flowers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-white border-2 border-rose-100 rounded-full focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all shadow-sm" data-id="element-2462" />
            </div>
          </div>

          {/* Product Count */}
          <div className="mb-8 flex justify-between items-center" data-id="element-2463">
            <p className="text-gray-600" data-id="element-2464">
              <span className="font-semibold text-gray-900" data-id="element-2465">
                {filteredProducts.length}
              </span>{' '}
              arrangement{filteredProducts.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-2466">
              {filteredProducts.map((product, index) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-rose-50" data-id="element-2467">
                  <div className="relative aspect-square overflow-hidden bg-gray-100" data-id="element-2468">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" data-id="element-2469" />
                    {product.isNew && <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-rose-500 shadow-sm" data-id="element-2470">
                        NEW
                      </div>}
                    <div className="absolute top-4 right-4 opacity-0 transform translate-x-4 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" data-id="element-2471">
                      <button className="p-2 bg-white rounded-full shadow-md text-gray-400 hover:text-rose-500 transition-colors" data-id="element-2472">
                        <Heart className="w-5 h-5" data-id="element-2473" />
                      </button>
                    </div>
                  </div>

                  <div className="p-5" data-id="element-2474">
                    <div className="text-xs font-medium text-rose-500 mb-1 uppercase tracking-wider" data-id="element-2475">
                      {product.category}
                    </div>
                    <h3 className="font-serif text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]" data-id="element-2476">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between mb-4" data-id="element-2477">
                      <span className="text-xl font-bold text-gray-900" data-id="element-2478">
                        {formatZAR(product.price)}
                      </span>
                      {product.stock > 0 ? <span className="text-xs text-green-600 font-medium" data-id="element-2479">
                          In Stock
                        </span> : <span className="text-xs text-red-600 font-medium" data-id="element-2480">
                          Out of Stock
                        </span>}
                    </div>

                    <Button onClick={() => handleCustomize(product.id)} className="w-full" size="sm" leftIcon={<PenTool className="w-4 h-4" data-id="element-2482" />} data-id="element-2481">
                      Customize This
                    </Button>
                  </div>
                </motion.div>)}
            </div> : <div className="text-center py-16" data-id="element-2483">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2484">
                <Search className="w-8 h-8 text-rose-400" data-id="element-2485" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2486">
                No flowers found
              </h3>
              <p className="text-gray-600" data-id="element-2487">
                Try adjusting your search to find what you're looking for.
              </p>
            </div>}
        </div>
      </section>

      {/* Why Choose Our Flowers */}
      <section className="py-16 bg-white" data-id="element-2488">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2489">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2490">
            {[{
            icon: <Flower2 className="w-6 h-6" data-id="element-2491" />,
            title: 'Fresh Daily Delivery',
            description: 'Sourced directly from local growers for maximum freshness and longevity'
          }, {
            icon: <PenTool className="w-6 h-6" data-id="element-2492" />,
            title: 'Custom Arrangements',
            description: 'Work with our florists to create bespoke bouquets for your special occasion'
          }, {
            icon: <Sparkles className="w-6 h-6" data-id="element-2493" />,
            title: 'Perfect for Any Occasion',
            description: 'From birthdays to anniversaries, express your feelings with flowers'
          }].map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="text-center p-6" data-id="element-2494">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-rose-100 rounded-full text-rose-500 mb-4" data-id="element-2495">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-2" data-id="element-2496">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm" data-id="element-2497">{feature.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-white to-rose-50/30" data-id="element-2498">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2499">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-2500">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-2501">
              <Heart className="w-8 h-8 text-rose-500" data-id="element-2502" />
            </div>

            <h2 className="font-serif text-4xl font-bold text-gray-900 mb-4" data-id="element-2503">
              Say It With Flowers
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-2504">
              Let us help you express what words cannot. Our florists are ready
              to create something beautiful just for you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2505">
              <Button size="lg" onClick={() => document.getElementById('products')?.scrollIntoView({
              behavior: 'smooth'
            })} rightIcon={<Search className="w-5 h-5" data-id="element-2507" />} data-id="element-2506">
                Browse Flowers
              </Button>
              <Button variant="outline" size="lg" onClick={() => navigate('/about')} data-id="element-2508">
                Learn More About Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-2509" />
    </div>;
}